/**
 * ExtractedStepsView — Parsed Feedback view for runs whose prompt declares
 * `outputSchemaType: 'ExtractedStepsV1'` (i.e. `extract_steps`). These
 * runs don't produce a `Feedback` object; they produce an
 * `ExtractedStepsData` object on `ModelRun.extractedData`.
 *
 * Renders:
 *   - Work Quality + confidence
 *   - Student Answer
 *   - Per-step list with confidence and crossed-out flag
 */
import { LatexText } from '@shared-ui/latex';
import { ConfidenceBadge, confidenceClass } from './confidenceTint';

/** Shape of one parsed extracted-step. Local to shared-ui's
 *  parsed-feedback module so this view doesn't take a host-app type. */
export interface ExtractedStepLike {
    stepNumber: number;
    content: string;
    confidence: number;
    crossedOut: boolean;
}

/** ExtractedSteps payload as parsed by the backend's
 *  `parseLlmExtraction` (see shared/llmConverters.ts). Structurally
 *  matches admin-console's `ExtractedStepsData` and the runtime
 *  `ExtractedSteps` from modelTypes — both pass through unchanged. */
export interface ExtractedStepsLike {
    workQuality?: string;
    workQualityConfidence?: number;
    steps: ExtractedStepLike[];
    studentAnswer?: string;
}

interface Props {
    data: ExtractedStepsLike;
}

export function ExtractedStepsView({ data }: Props) {
    return (
        <div className="feedback-content">
            <div className="feedback-sections" style={{ marginBottom: 12 }}>
                <div className="feedback-section">
                    <h4>Work Quality</h4>
                    <p>
                        {data.workQuality ?? '-'}
                        {' '}
                        <ConfidenceBadge value={data.workQualityConfidence} />
                    </p>
                </div>
                <div className="feedback-section">
                    <h4>Student Answer</h4>
                    {data.studentAnswer
                        ? <p><LatexText text={data.studentAnswer} /></p>
                        : <p className="empty">-</p>}
                </div>
            </div>
            {data.steps?.length ? (
                <div className="sa-steps-analysis">
                    <h4 className="sa-title">Extracted Steps</h4>
                    <div className="sa-steps-list">
                        {data.steps.map(s => (
                            <div
                                key={s.stepNumber}
                                className={`sa-step-item ${s.crossedOut ? 'incorrect' : 'correct'} ${confidenceClass(s.confidence)}`}
                            >
                                <div className="sa-step-rail">Step {s.stepNumber}</div>
                                <div className="sa-step-body">
                                    <div className="sa-step-content">
                                        <LatexText text={s.content} />
                                    </div>
                                    <div className="extracted-step-meta">
                                        <ConfidenceBadge value={s.confidence} />
                                        {s.crossedOut && <span className="crossed-out">crossed out</span>}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            ) : (
                <p className="empty">No steps extracted</p>
            )}
        </div>
    );
}
